import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function Hero() {
  const videoRef = useRef(null);
  const { t } = useLanguage();

  // Asme-style seamless video crossfade loop
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let fadeId;
    const fadeTo = (from, to, duration, onDone) => {
      const start = performance.now();
      const step = (now) => {
        const p = Math.min((now - start) / duration, 1);
        video.style.opacity = from + (to - from) * p;
        if (p < 1) { fadeId = requestAnimationFrame(step); }
        else { video.style.opacity = to; onDone && onDone(); }
      };
      cancelAnimationFrame(fadeId);
      fadeId = requestAnimationFrame(step);
    };

    const onCanPlay = () => {
      video.play().catch(() => {});
      fadeTo(0, 1, 500, null);
    };

    const onTimeUpdate = () => {
      if (video.duration && video.currentTime >= video.duration - 0.55) {
        fadeTo(parseFloat(video.style.opacity || 1), 0, 500, null);
      }
    };

    const onEnded = () => {
      video.style.opacity = 0;
      setTimeout(() => {
        video.currentTime = 0;
        video.play().catch(() => {});
        fadeTo(0, 1, 500, null);
      }, 100);
    };

    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);
    video.style.opacity = 0;

    return () => {
      cancelAnimationFrame(fadeId);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="inicio"
      className="relative min-h-screen overflow-hidden flex flex-col"
      style={{ background: '#000' }}
    >
      {/* Background video */}
      <video
        ref={videoRef}
        src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260405_074625_a81f018a-956b-43fb-9aee-4d1508e30e6a.mp4"
        muted
        autoPlay
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover object-bottom"
        style={{ opacity: 0, zIndex: 0 }}
      />

      {/* Overlays */}
      <div className="absolute inset-0 z-1" style={{ background: 'rgba(0,0,0,0.55)' }} />
      <div className="absolute inset-0 z-1" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 50%, rgba(0,0,0,0.3) 100%)' }} />

      {/* Navbar space (60px) */}
      <div className="h-16 flex-shrink-0" />

      {/* Hero content */}
      <div
        className="relative flex-1 flex flex-col items-center justify-center px-6 py-12 text-center"
        style={{ zIndex: 10, transform: 'translateY(-5%)' }}
      >
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-6"
        >
          <span className="v-tag green">
            <span className="live-dot" />
            {t('hero.tag')}
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold text-white tracking-tight whitespace-nowrap mb-4"
          style={{ fontFamily: "'Instrument Serif', serif", lineHeight: 1.05 }}
        >
          {t('hero.headline1')}{' '}
          <em className="italic" style={{ color: 'rgba(255,255,255,0.65)' }}>{t('hero.headline2')}</em>
          <br />
          {t('hero.headline3')}{' '}
          <em className="italic" style={{ color: '#8DC21F' }}>{t('hero.headline4')}</em>
        </motion.h1>

        {/* Sub */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="text-sm md:text-base text-white/55 leading-relaxed max-w-[52ch] mb-8 px-4"
        >
          {t('hero.sub')}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="flex flex-wrap items-center justify-center gap-3 mb-12"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollTo('#proyectos')}
            className="liquid-glass rounded-full px-8 py-3 text-white text-sm font-semibold hover:bg-white/5 flex items-center gap-2"
            style={{ border: 'none', cursor: 'pointer' }}
          >
            {t('hero.cta1')}
            <ArrowRight size={15} />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollTo('#retos')}
            className="rounded-full px-8 py-3 text-sm font-bold flex items-center gap-2"
            style={{ background: '#8DC21F', color: '#000', border: 'none', boxShadow: '0 6px 24px rgba(141,194,31,0.3)', cursor: 'pointer' }}
          >
            {t('hero.cta2')}
          </button>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
        >
          <div
            className="liquid-glass rounded-2xl px-6 py-4 flex flex-wrap items-center justify-center gap-6 md:gap-10"
            style={{ border: 'none' }}
          >
            {[
              { v: t('hero.stat1v'), l: t('hero.stat1l') },
              { v: t('hero.stat2v'), l: t('hero.stat2l') },
              { v: t('hero.stat3v'), l: t('hero.stat3l') },
              { v: t('hero.stat4v'), l: t('hero.stat4l') },
            ].map(({ v, l }) => (
              <div key={l} className="text-center">
                <div className="text-lg md:text-xl font-bold text-white">{v}</div>
                <div className="text-[10px] text-white/45 mt-0.5 uppercase tracking-wide">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="relative z-10 flex flex-col items-center gap-2 pb-8"
      >
        <span className="text-[9px] uppercase tracking-[0.25em] text-white/30">Scroll</span>
        <motion.div
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <ArrowDown size={13} className="text-white/30" />
        </motion.div>
      </motion.div>
    </section>
  );
}
